import { getTokenFromCookie } from './cookies'
import { isImpersonationToken } from './token'

interface AuthFetchOptions {
  getSessionToken?: () => null | string
  getSessionVersion?: () => number
  onAuthError?: (_sessionVersion?: number) => Promise<void> | void
  onImpersonationExpired?: (_sessionVersion?: number) => Promise<void> | void
  refreshToken: () => Promise<null | string>
}

/** An obsolete refresh belongs to a prior session and must not cause logout. */
export class AuthRefreshCancelledError extends Error {
  constructor() {
    super('Auth refresh was cancelled because its session changed')
    this.name = 'AuthRefreshCancelledError'
  }
}

/** Creates a fetch wrapper that handles 401 -> refresh -> retry. */
export function createAuthFetch({
  getSessionToken,
  getSessionVersion,
  onAuthError,
  onImpersonationExpired,
  refreshToken,
}: AuthFetchOptions) {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Server renders have no cookie jar: send the request's session token and leave refresh to the browser
    const isServer = !('document' in globalThis)
    const sessionVersion = getSessionVersion?.()
    const token = isServer ? (getSessionToken?.() ?? null) : getTokenFromCookie()
    const ownedToken = getSessionToken?.()
    const ownsDispatch = getSessionToken !== undefined && ownedToken === token
    const isCurrentRequest = () => {
      return (
        token === getTokenFromCookie() &&
        (sessionVersion === undefined || sessionVersion === getSessionVersion?.())
      )
    }
    const isCurrentSession = () => {
      return sessionVersion === undefined || sessionVersion === getSessionVersion?.()
    }
    const headers = new Headers(init?.headers)

    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    headers.set('Accept', 'application/json')
    const response = await fetch(input, { ...init, headers })

    const getRotatedToken = () => {
      const currentToken = getSessionToken?.()
      if (!currentToken) return null
      return ownsDispatch &&
        token !== null &&
        currentToken !== token &&
        !isImpersonationToken(token) &&
        !isImpersonationToken(currentToken) &&
        sessionVersion !== undefined &&
        sessionVersion === getSessionVersion?.() &&
        getTokenFromCookie() === currentToken
        ? currentToken
        : null
    }

    if (response.status !== 401 || isServer) {
      return response
    }

    if (!isCurrentRequest()) {
      const currentToken = getRotatedToken()
      if (!currentToken) return response
      headers.set('Authorization', `Bearer ${currentToken}`)
      return fetch(input, { ...init, headers })
    }

    if (token && isImpersonationToken(token)) {
      await onImpersonationExpired?.(sessionVersion)
      return response
    }

    let newToken: null | string

    try {
      newToken = await refreshToken()
    } catch (error) {
      if (error instanceof AuthRefreshCancelledError) {
        return response
      }

      throw error
    }

    if (!isCurrentSession()) return response

    if (newToken) {
      if (getTokenFromCookie() !== newToken) return response

      headers.set('Authorization', `Bearer ${newToken}`)
      return fetch(input, { ...init, headers })
    }

    await onAuthError?.(sessionVersion)
    return response
  }
}
